import {
  KpiFeedbackChoiceCategory,
  KpiNextActionInput,
  KpiNextActionResultChoice,
  KpiPreviousNextActionResultInput,
  UpdateKpiFeedbackSheetAssessmentInput,
  UpdateKpiFeedbackSheetInput,
} from '../../../../_generated/graphql'
import {
  KpiDashboardKeyAction,
  KpiFeedbackChoiceView,
  KpiFeedbackChoices,
  KpiFeedbackSheetAssessmentView,
  KpiFeedbackSheetCandidateView,
  KpiFeedbackSheetCandidates,
  KpiFeedbackSheetView,
  KpiPreviousNextActionView,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import {
  compareNullableNumbers,
  SortDirection,
} from '../operator-list/operator-list.logic'

export type FeedbackSheetEditableField =
  | 'interviewDate'
  | 'consultationTopics'
  | 'operatorSelfIssues'
  | 'managerHypothesis'
  | 'otherNotes'

export type FeedbackAssessmentTextEditableField =
  | 'goodPoints'
  | 'improvementPoints'

export type OperatorFeedbackAssessmentEditableField =
  | 'operatorGoodPoints'
  | 'operatorImprovementPoints'

export type FeedbackAssessmentEditableField =
  | FeedbackAssessmentTextEditableField
  | OperatorFeedbackAssessmentEditableField

export type FeedbackSaveField =
  | FeedbackSheetEditableField
  | FeedbackAssessmentEditableField
  | 'previousNextActionResult'
  | 'responseStatus'
  | 'nextActions'

export interface FeedbackAssessmentChangePreservation {
  keptBlockIds: ReadonlySet<string>
  preservedFields: ReadonlySet<FeedbackSaveField>
}

/** ブロック ID の差分から、サーバーが対象変更後も維持するブロックとシート欄を返す。 */
export function getFeedbackAssessmentChangePreservation(
  current: readonly Pick<KpiFeedbackSheetAssessmentView, 'sheetAssessmentId'>[],
  latest: readonly Pick<KpiFeedbackSheetAssessmentView, 'sheetAssessmentId'>[],
): FeedbackAssessmentChangePreservation {
  const currentIds = new Set(
    current.map(({ sheetAssessmentId }) => sheetAssessmentId),
  )
  const keptBlockIds = new Set(
    latest
      .filter(({ sheetAssessmentId }) => currentIds.has(sheetAssessmentId))
      .map(({ sheetAssessmentId }) => sheetAssessmentId),
  )
  const hasAdded = latest.some(
    ({ sheetAssessmentId }) => !currentIds.has(sheetAssessmentId),
  )
  const preservedFields = new Set<FeedbackSaveField>([
    'consultationTopics',
    'operatorSelfIssues',
    'interviewDate',
    'previousNextActionResult',
  ])
  if (keptBlockIds.size > 0) {
    preservedFields.add('managerHypothesis')
    preservedFields.add('otherNotes')
  }
  if (!hasAdded) preservedFields.add('responseStatus')
  return { keptBlockIds, preservedFields }
}

type KpiFeedbackFieldEditor = 'operator' | 'manager'

// backend/src/datasources/kpiFeedbackSheet.logic.ts の分類マップに対応する。
// GraphQL input 型の全キーを分類し、生成型更新時に分類漏れをコンパイルエラーにする。
const SHEET_FIELD_EDITORS: Record<
  keyof UpdateKpiFeedbackSheetInput,
  KpiFeedbackFieldEditor
> = {
  interviewDate: 'manager',
  consultationTopics: 'operator',
  operatorSelfIssues: 'operator',
  managerHypothesis: 'manager',
  otherNotes: 'manager',
}
const ASSESSMENT_FIELD_EDITORS: Record<
  keyof UpdateKpiFeedbackSheetAssessmentInput,
  KpiFeedbackFieldEditor
> = {
  operatorGoodPoints: 'operator',
  operatorImprovementPoints: 'operator',
  goodPoints: 'manager',
  improvementPoints: 'manager',
  nextActions: 'manager',
}

/** シート本体のフィールドが主任専用の記入欄か返す。 */
export function isManagerOnlySheetField(
  field: keyof UpdateKpiFeedbackSheetInput,
): boolean {
  return SHEET_FIELD_EDITORS[field] === 'manager'
}

/** ネクストアクションを含むブロックのフィールドが主任専用の記入欄か返す。 */
export function isManagerOnlyAssessmentField(
  field: keyof UpdateKpiFeedbackSheetAssessmentInput,
): boolean {
  return ASSESSMENT_FIELD_EDITORS[field] === 'manager'
}

/** オペレーター記入欄の選択肢 1 件の編集値。 */
export interface EditableFeedbackChoiceValue {
  choiceId: string
  selected: boolean
}

/** オペレーター記入欄の選択肢と自由文の編集値。 */
export interface EditableFeedbackPointsValue {
  choices: EditableFeedbackChoiceValue[]
  freeText: string
}

/** 指定カテゴリの回答選択肢を sortOrder 順で返す。 */
export function filterFeedbackChoices(
  choices: KpiFeedbackChoices,
  category: KpiFeedbackChoiceCategory,
): KpiFeedbackChoiceView[] {
  return choices
    .filter((choice) => choice.category === category)
    .sort((left, right) => left.sortOrder - right.sortOrder)
}

/** フィードバックシートの対象アセスメント上限。 */
export const MAX_FEEDBACK_TARGET_COUNT = 2

export interface PreviousNextActionRow {
  key: string
  nextActionId: string
  sheetAssessmentId: string
  sheetNumber: number
  isClosed: boolean
  number: number
  text: string
  resultLabel: string | null
  hasPersistedResult: boolean
  persistedResultLabel: string | null
  resultNote: string | null
  resultClass: 'success' | 'warning' | 'error' | 'other' | 'waiting' | 'closed'
}

export interface KeyActionScoreRow {
  keyActionId: string
  keyActionName: string
  score: number
}

type NextActionResult = NonNullable<
  KpiFeedbackSheetAssessmentView['nextActions'][number]['result']
>

/** ネクストアクション結果の選択値と表示ラベルの共有マスタ。 */
export const KPI_NEXT_ACTION_RESULT_CHOICES = [
  {
    value: KpiNextActionResultChoice.Practiced,
    label: '実践できた',
  },
  {
    value: KpiNextActionResultChoice.PartiallyPracticed,
    label: '意識できたが実践は不十分だった',
  },
  {
    value: KpiNextActionResultChoice.NotConscious,
    label: '意識できなかった',
  },
  {
    value: KpiNextActionResultChoice.Other,
    label: 'その他',
  },
] as const

/**
 * 回答済み choice ID を固定マスタのラベルへ解決する。
 * 無効化後の ID が保存済みの過去データに残る場合は、表示継続のためその ID だけをスキップする。
 */
export function resolveFeedbackChoiceLabels(
  choiceIds: readonly string[],
  choices: KpiFeedbackChoices,
): string[] {
  return choiceIds.flatMap((choiceId) => {
    const choice = choices.find((candidate) => candidate.id === choiceId)
    if (choice == null) {
      return []
    }
    return [choice.label]
  })
}

/** 前回ネクストアクションを結果表示用の行へ変換する。 */
export function buildPreviousNextActionRows(
  previousSheet: KpiFeedbackSheetView['previousSheet'],
): PreviousNextActionRow[] {
  if (previousSheet == null) {
    return []
  }

  return numberPreviousNextActions(previousSheet.nextActions).map(
    ({ action, number }) => {
      const isClosed = action.closedAt != null
      const row = {
        key: `${action.sheetAssessmentId}:${action.id}`,
        number,
        nextActionId: action.id,
        sheetAssessmentId: action.sheetAssessmentId,
        sheetNumber: action.sheetNumber,
        isClosed,
        text: action.text,
      }
      if (action.result == null || isPreviousNextActionReopened(action)) {
        return {
          ...row,
          resultLabel: isClosed ? '完了' : null,
          hasPersistedResult: false,
          persistedResultLabel: null,
          resultNote: null,
          resultClass: isClosed ? 'closed' : 'waiting',
        }
      }
      const display = nextActionResultDisplay(action.result.choice)
      return {
        ...row,
        resultLabel: display.label,
        hasPersistedResult: true,
        persistedResultLabel:
          action.result.choice === KpiNextActionResultChoice.Other
            ? `${display.label}: ${resolveOtherResultNote(action.result)}`
            : display.label,
        resultNote: resolveOtherResultNote(action.result),
        resultClass: display.resultClass,
      }
    },
  )
}

/** 前回ネクストアクションをシート番号・position 順で返し、同値は配列順を維持する。 */
export function orderPreviousNextActions<
  T extends { sheetNumber: number; position: number },
>(actions: readonly T[]): T[] {
  return [...actions].sort(
    (left, right) =>
      left.sheetNumber - right.sheetNumber || left.position - right.position,
  )
}

/** クローズ済みを含む全体順の番号を付け、表示先によらず同じ番号を返す。 */
export function numberPreviousNextActions<
  T extends { sheetNumber: number; position: number },
>(actions: readonly T[]): { action: T; number: number }[] {
  return orderPreviousNextActions(actions).map((action, index) => ({
    action,
    number: index + 1,
  }))
}

export type CandidateSortField =
  | 'assessDate'
  | 'overallScore'
  | 'focusKeyActionAverageScore'

export type CandidateSort = {
  field: CandidateSortField
  direction: SortDirection
}

export const DEFAULT_CANDIDATE_SORT: Readonly<CandidateSort> = {
  field: 'assessDate',
  direction: 'desc',
}

/** 元の配列と行参照を維持し、同値は順位順、未算出のスコアは常に末尾にして表示用配列を返す。 */
export function sortCandidateRows<
  T extends {
    rank: number
    candidate: Pick<
      KpiFeedbackSheetCandidateView,
      'assessDate' | 'overallScore' | 'focusKeyActionAverageScore'
    >
  },
>(rows: readonly T[], sort: Readonly<CandidateSort>): T[] {
  const direction = sort.direction === 'asc' ? 1 : -1
  return [...rows].sort((left, right) => {
    let difference: number
    switch (sort.field) {
      case 'assessDate':
        difference =
          (left.candidate.assessDate.getTime() -
            right.candidate.assessDate.getTime()) *
          direction
        break
      case 'focusKeyActionAverageScore': {
        const leftScore = left.candidate.focusKeyActionAverageScore
        const rightScore = right.candidate.focusKeyActionAverageScore
        if (
          typeof leftScore === 'undefined' ||
          typeof rightScore === 'undefined'
        ) {
          throw new Error('Feedback focus key action average score is missing')
        }
        difference = compareNullableNumbers(
          leftScore,
          rightScore,
          sort.direction,
        )
        break
      }
      case 'overallScore': {
        const leftScore = left.candidate.overallScore
        const rightScore = right.candidate.overallScore
        if (
          typeof leftScore === 'undefined' ||
          typeof rightScore === 'undefined'
        ) {
          throw new Error('Feedback overall score is missing')
        }
        difference = compareNullableNumbers(
          leftScore,
          rightScore,
          sort.direction,
        )
        break
      }
    }
    return difference === 0 ? left.rank - right.rank : difference
  })
}

/** 前回シート作成以降の候補から候補順に最大 2 件の自動提案 ID を返す。 */
export function automaticCandidateIds(
  candidates: KpiFeedbackSheetCandidates,
): string[] {
  return candidates
    .filter((candidate) => candidate.isAfterLatestSheet)
    .slice(0, MAX_FEEDBACK_TARGET_COUNT)
    .map((candidate) => candidate.assessmentId)
}

/** 選択済みなら解除し、未選択なら上限まで追加する。空 ID・上限超過はエラーにする。 */
export function toggleCandidateSelection(
  selectedIds: readonly string[],
  assessmentId: string,
): string[] {
  if (assessmentId.trim() === '') {
    throw new Error('Assessment ID is empty')
  }
  if (selectedIds.includes(assessmentId)) {
    return selectedIds.filter((id) => id !== assessmentId)
  }
  if (selectedIds.length >= MAX_FEEDBACK_TARGET_COUNT) {
    // UI はコンポーネント側で通知して到達させない。ここは直接呼び出しに対する防御。
    throw new Error(
      `Feedback target count exceeds ${MAX_FEEDBACK_TARGET_COUNT}`,
    )
  }
  return [...selectedIds, assessmentId]
}

/** 選択 ID を候補表の順に並べる。候補にない ID はエラーにする。 */
export function orderSelectedCandidateIds(
  candidates: KpiFeedbackSheetCandidates,
  selectedIds: readonly string[],
): string[] {
  for (const id of selectedIds) {
    if (!candidates.some((candidate) => candidate.assessmentId === id)) {
      throw new Error(`Feedback candidate is missing: ${id}`)
    }
  }
  return candidates
    .filter((candidate) => selectedIds.includes(candidate.assessmentId))
    .map((candidate) => candidate.assessmentId)
}

/** 自動提案は前回シート以降の順位、手動選択は全体順位と主指標から選出理由を返す。 */
export function describeCandidateSelectionReason(
  candidates: KpiFeedbackSheetCandidates,
  automaticIds: readonly string[],
  assessmentId: string,
): string {
  const index = candidates.findIndex(
    (candidate) => candidate.assessmentId === assessmentId,
  )
  if (index === -1) {
    throw new Error(`Feedback candidate is missing: ${assessmentId}`)
  }

  const isAutomatic = automaticIds.includes(assessmentId)
  const rankText = isAutomatic
    ? `自動提案: 前回シート以降で ${automaticIds.indexOf(assessmentId) + 1} 位`
    : `手動選択: 候補 ${index + 1} 位`
  const candidate = candidates[index]
  let detail: string
  if (candidates.some((item) => item.focusKeyActionAverageScore != null)) {
    detail =
      candidate.focusKeyActionAverageScore == null
        ? '重点KA未評価のため下位'
        : `重点KA平均 ${candidate.focusKeyActionAverageScore.toFixed(2)}`
  } else {
    detail =
      candidate.overallScore == null
        ? 'スコア未算出'
        : `総合 ${formatFeedbackOverallScore(candidate.overallScore)}`
  }
  return `${rankText}（${detail}）`
}

/** シート本体の変更項目だけを Update input に変換する。 */
export function buildFeedbackSheetUpdateInput(
  field: FeedbackSheetEditableField,
  editedValue: string,
  persistedSheet: KpiFeedbackSheetView,
): UpdateKpiFeedbackSheetInput | null {
  if (field === 'interviewDate') {
    const persistedValue = formatDateInputValue(persistedSheet.interviewDate)
    if (editedValue === persistedValue) {
      return null
    }
    return {
      interviewDate:
        editedValue === '' ? null : parseDateInputValue(editedValue),
    }
  }

  const persistedValue = normalizeNullableText(
    persistedSheet[field],
    'Feedback text field is missing',
  )
  if (editedValue === persistedValue) {
    return null
  }

  return { [field]: editedValue }
}

/** アセスメントブロックの変更項目だけを Update input に変換する。 */
export function buildFeedbackAssessmentUpdateInput(
  field: FeedbackAssessmentTextEditableField,
  editedValue: string,
  persistedAssessment: KpiFeedbackSheetAssessmentView,
): UpdateKpiFeedbackSheetAssessmentInput | null {
  if (
    editedValue ===
    normalizeNullableText(
      persistedAssessment[field],
      'Feedback text field is missing',
    )
  ) {
    return null
  }

  return { [field]: editedValue }
}

/** オペレーター記入欄の変更項目だけを Update input に変換する。 */
export function buildOperatorFeedbackPointsUpdateInput(
  field: OperatorFeedbackAssessmentEditableField,
  editedValue: EditableFeedbackPointsValue,
  persistedAssessment: KpiFeedbackSheetAssessmentView,
): UpdateKpiFeedbackSheetAssessmentInput | null {
  const availableChoiceIds = new Set(
    editedValue.choices.map(({ choiceId }) => choiceId),
  )
  const editedPoints = normalizeFeedbackPoints(
    editedValue.choices
      .filter(({ selected }) => selected)
      .map(({ choiceId }) => choiceId),
    editedValue.freeText,
  )
  const persistedPoints = persistedAssessment[field]
  if (typeof persistedPoints === 'undefined') {
    throw new Error('Feedback operator points are missing')
  }
  const normalizedPersistedPoints =
    persistedPoints == null
      ? null
      : normalizeFeedbackPoints(
          persistedPoints.choiceIds.filter((choiceId) =>
            availableChoiceIds.has(choiceId),
          ),
          persistedPoints.freeText,
        )
  if (haveSameFeedbackPoints(editedPoints, normalizedPersistedPoints)) {
    return null
  }

  return field === 'operatorGoodPoints'
    ? { operatorGoodPoints: editedPoints }
    : { operatorImprovementPoints: editedPoints }
}

/** ネクストアクション全量に変更がある場合だけ Update input を返す。 */
export function buildNextActionsUpdateInput(
  editedActions: readonly KpiNextActionInput[],
  persistedAssessment: KpiFeedbackSheetAssessmentView,
): UpdateKpiFeedbackSheetAssessmentInput | null {
  const nonEmptyActions = editedActions.filter(
    (action) => action.text.trim() !== '',
  )
  const unchanged =
    nonEmptyActions.length === persistedAssessment.nextActions.length &&
    nonEmptyActions.every((action, index) => {
      const persistedAction = persistedAssessment.nextActions[index]
      return (
        action.id === persistedAction.id && action.text === persistedAction.text
      )
    })

  return unchanged ? null : { nextActions: nonEmptyActions }
}

/** KeyAction スコアをダッシュボード定義の名称付き表示行へ変換する。 */
export function buildKeyActionScoreRows(
  assessment: KpiFeedbackSheetAssessmentView,
  keyActions: readonly KpiDashboardKeyAction[],
): KeyActionScoreRow[] {
  return assessment.keyActionScores.map((score) => {
    const keyAction = keyActions.find(
      (candidate) => candidate.id === score.keyActionId,
    )
    if (keyAction == null) {
      throw new Error(`KPI key action is missing: ${score.keyActionId}`)
    }
    return {
      keyActionId: score.keyActionId,
      keyActionName: keyAction.name,
      score: score.score,
    }
  })
}

/** 重点 KeyAction の平均スコアを算出する。 */
export function calculateKeyActionAverage(
  assessment: KpiFeedbackSheetAssessmentView,
): number | null {
  if (assessment.keyActionScores.length === 0) {
    return null
  }
  const total = assessment.keyActionScores.reduce(
    (sum, score) => sum + score.score,
    0,
  )
  return total / assessment.keyActionScores.length
}

/** ネクストアクションの結果とクローズ状態を表示文言へ変換する。 */
export function formatNextActionStatus(
  action: KpiFeedbackSheetAssessmentView['nextActions'][number],
): string | null {
  const resultText = formatNextActionResult(action.result)
  if (action.closedAt == null) {
    return resultText
  }
  return resultText == null ? '完了' : `${resultText}（完了）`
}

/** 保存済みネクストアクション結果をラベルと備考の表示文言へ変換する。 */
export function formatNextActionResult(
  result: KpiFeedbackSheetAssessmentView['nextActions'][number]['result'],
): string | null {
  if (result == null) {
    return null
  }
  const display = nextActionResultDisplay(result.choice)
  const note = resolveOtherResultNote(result)
  return note == null ? display.label : `${display.label}：${note}`
}

/** GraphQL Date を YYYY/MM/DD 表示へ整形する。 */
export function formatFeedbackDate(value: Date): string {
  return formatTokyoDateParts(toTokyoDate(value))
}

/** GraphQL Date を YYYY/MM/DD HH:mm 表示へ整形する。 */
export function formatFeedbackDateTime(value: Date): string {
  const date = toTokyoDate(value)
  return `${formatTokyoDateParts(date)} ${padDatePart(
    date.getUTCHours(),
  )}:${padDatePart(date.getUTCMinutes())}`
}

/** 商談名が空の場合は未設定表示へ整形する。 */
export function formatFeedbackMeetingTitle(meetingTitle: string): string {
  return meetingTitle === '' ? '（商談名未設定）' : meetingTitle
}

/** 商談名を含むアセスメントリンクの読み上げ文言を返す。 */
export function buildAssessmentLinkLabel(meetingTitleText: string): string {
  return `${meetingTitleText}のアセスメントを開く（新しいタブ）`
}

/** 総合スコアを小数点以下 2 桁で切り上げた表示文言へ整形する。 */
export function formatFeedbackOverallScore(
  score: number | null | undefined,
): string {
  if (typeof score === 'undefined') {
    throw new Error('Feedback overall score is missing')
  }
  if (score == null) {
    return '-'
  }
  const scaled = Number((score * 100).toPrecision(12))
  return `${(Math.ceil(scaled) / 100).toFixed(2)}点`
}

/** nullable GraphQL Date を date input 用 YYYY-MM-DD へ整形する。 */
export function formatDateInputValue(value: Date | null | undefined): string {
  if (typeof value === 'undefined') {
    throw new Error('Feedback date is missing')
  }
  if (value == null) {
    return ''
  }
  const date = parseGraphqlDate(value)
  return `${date.getUTCFullYear()}-${padDatePart(
    date.getUTCMonth() + 1,
  )}-${padDatePart(date.getUTCDate())}`
}

/** ネクストアクション結果の文言と表示種別を返す。 */
function nextActionResultDisplay(choice: KpiNextActionResultChoice): {
  label: string
  resultClass: PreviousNextActionRow['resultClass']
} {
  const resultChoice = KPI_NEXT_ACTION_RESULT_CHOICES.find(
    ({ value }) => value === choice,
  )
  if (resultChoice == null) {
    throw new Error(`KPI next action result choice is invalid: ${choice}`)
  }

  switch (choice) {
    case KpiNextActionResultChoice.Practiced:
      return { label: resultChoice.label, resultClass: 'success' }
    case KpiNextActionResultChoice.PartiallyPracticed:
      return {
        label: resultChoice.label,
        resultClass: 'warning',
      }
    case KpiNextActionResultChoice.NotConscious:
      return { label: resultChoice.label, resultClass: 'error' }
    case KpiNextActionResultChoice.Other:
      return { label: resultChoice.label, resultClass: 'other' }
  }
}

/** nullable 文字列を編集コントロールの値へ変換する。 */
export function normalizeNullableText(
  value: string | null | undefined,
  missingValueMessage: string,
): string {
  if (typeof value === 'undefined') {
    throw new Error(missingValueMessage)
  }
  return value == null ? '' : value
}

/** OTHER 結果の備考を検証して返す。 */
function resolveOtherResultNote(result: NextActionResult): string | null {
  if (result.choice !== KpiNextActionResultChoice.Other) {
    return null
  }
  if (typeof result.note === 'undefined') {
    throw new Error('KPI next action result note is missing')
  }
  return result.note
}

/** date input の値を UTC 日付へ変換する。 */
function parseDateInputValue(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match == null) {
    throw new Error(`Feedback interview date is invalid: ${value}`)
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    throw new Error(`Feedback interview date is invalid: ${value}`)
  }
  return date
}

/** GraphQL transport 由来の日付を検証して Date に変換する。 */
function parseGraphqlDate(value: Date): Date {
  const date =
    value instanceof Date ? new Date(value.getTime()) : new Date(value)
  if (!Number.isFinite(date.getTime())) {
    throw new Error('Feedback date is invalid')
  }
  return date
}

/** GraphQL Date を Asia/Tokyo 固定の日時要素を持つ Date へ変換する。 */
function toTokyoDate(value: Date): Date {
  const date = parseGraphqlDate(value)
  return new Date(date.getTime() + 9 * 60 * 60 * 1000)
}

/** Asia/Tokyo へ変換済みの Date を YYYY/MM/DD へ整形する。 */
function formatTokyoDateParts(date: Date): string {
  return `${date.getUTCFullYear()}/${padDatePart(
    date.getUTCMonth() + 1,
  )}/${padDatePart(date.getUTCDate())}`
}

/** 月日を 2 桁へ整形する。 */
function padDatePart(value: number): string {
  return String(value).padStart(2, '0')
}

/** 空回答を null に正規化したオペレーター記入値を返す。 */
function normalizeFeedbackPoints(choiceIds: string[], freeText: string) {
  if (choiceIds.length === 0 && freeText === '') {
    return null
  }
  return { choiceIds, freeText }
}

/** 正規化済みのオペレーター記入値が一致するかを返す。 */
function haveSameFeedbackPoints(
  left: ReturnType<typeof normalizeFeedbackPoints>,
  right: ReturnType<typeof normalizeFeedbackPoints>,
): boolean {
  if (left == null || right == null) {
    return left === right
  }
  return (
    left.freeText === right.freeText &&
    left.choiceIds.length === right.choiceIds.length &&
    left.choiceIds.every(
      (choiceId, index) => right.choiceIds[index] === choiceId,
    )
  )
}

/** 回答可能な前回ネクストアクション 1 件の結果フォーム値。 */
export interface ResponsePreviousNextActionValue {
  nextActionId: string
  choice: KpiNextActionResultChoice | null
  note: string
}

/** 「その他」自由記述コントロールへ反映する状態。 */
export interface NextActionNoteState {
  disabled: boolean
  note: string
}

/**
 * ネクストアクション結果の選択に応じて「その他」自由記述の状態を返す。
 * OTHER 以外へ切り替えた場合は、送信対象外の値を残さない。
 */
export function resolveNextActionNoteState(
  choice: KpiNextActionResultChoice | null,
  currentNote: string,
): NextActionNoteState {
  if (choice === KpiNextActionResultChoice.Other) {
    return { disabled: false, note: currentNote }
  }
  return { disabled: true, note: '' }
}

/** 回答可能な前回ネクストアクションと全体順の番号・フォーム初期値。 */
export interface AnswerablePreviousNextActionRow {
  action: NonNullable<
    KpiFeedbackSheetView['previousSheet']
  >['nextActions'][number]
  number: number
  value: ResponsePreviousNextActionValue
}

/** 未クローズまたは今回サイクル中にクローズした要素を全体順の番号付きで返す。 */
export function buildAnswerablePreviousNextActionRows(
  sheet: KpiFeedbackSheetView,
): AnswerablePreviousNextActionRow[] {
  if (sheet.previousSheet == null) {
    return []
  }
  const numberedActions = numberPreviousNextActions(
    sheet.previousSheet.nextActions,
  )
  return (
    numberedActions
      // backend の isNextActionCarriedTo と同じ条件。サーバー契約に対する防御的な再適用。
      .filter(
        ({ action }) =>
          action.closedAt == null || action.closedAt >= sheet.createdAt,
      )
      .map(({ action, number }) => {
        // 主任が再開した項目は前サイクルの「実践できた」を引きずらせず、本人に選び直させる
        const isReopened = isPreviousNextActionReopened(action)
        return {
          action,
          number,
          value: {
            nextActionId: action.id,
            choice:
              isReopened || action.result == null ? null : action.result.choice,
            note:
              isReopened || action.result == null
                ? ''
                : resolveInitialNextActionNote(
                    action.result.choice,
                    action.result.note,
                  ),
          },
        }
      })
  )
}

/** 保存済みのその他の記述を編集値へ変換する。 */
function resolveInitialNextActionNote(
  choice: KpiNextActionResultChoice,
  note: string | null | undefined,
): string {
  if (choice !== KpiNextActionResultChoice.Other) {
    return ''
  }
  if (typeof note === 'undefined') {
    throw new Error('KPI next action result note is missing')
  }
  return note == null ? '' : note
}

/** 未選択・記述待ち・保存済みと同一の結果を除き、変更した結果だけを返す。 */
export function buildPreviousNextActionResultInput(
  nextActionId: string,
  formValue: { choice: KpiNextActionResultChoice | null; note: string },
  persistedAction: KpiPreviousNextActionView,
): KpiPreviousNextActionResultInput | null {
  const { choice, note } = formValue
  if (
    choice == null ||
    (choice === KpiNextActionResultChoice.Other && note.trim() === '')
  ) {
    return null
  }
  const savedNote = choice === KpiNextActionResultChoice.Other ? note : null
  if (
    !isPreviousNextActionReopened(persistedAction) &&
    persistedAction.result?.choice === choice &&
    persistedAction.result.note === savedNote
  ) {
    return null
  }
  return { nextActionId, choice, note: savedNote }
}

/**
 * 入力値では対象の result だけを楽観適用し、closedAt を含む他の値は維持する。
 * サーバーの返却値では対象のアクション全体を差し替えて確定適用する。
 */
export function applyPreviousNextActionResult(
  sheet: KpiFeedbackSheetView,
  action: KpiPreviousNextActionView | KpiPreviousNextActionResultInput,
): KpiFeedbackSheetView {
  const previousSheet = sheet.previousSheet
  const actionId = 'nextActionId' in action ? action.nextActionId : action.id
  if (
    previousSheet == null ||
    !previousSheet.nextActions.some(({ id }) => id === actionId)
  ) {
    throw new Error(`Previous next action is missing: ${actionId}`)
  }
  return {
    ...sheet,
    previousSheet: {
      ...previousSheet,
      nextActions: previousSheet.nextActions.map((previous) =>
        previous.id !== actionId
          ? previous
          : 'nextActionId' in action
            ? {
                ...previous,
                result: { choice: action.choice, note: action.note },
              }
            : action,
      ),
    },
  }
}

/** 管理者が PRACTICED のアクションを再開し、今回の回答を待っているかを返す。 */
export function isPreviousNextActionReopened(
  action: KpiPreviousNextActionView,
): boolean {
  return (
    action.closedAt == null &&
    action.result?.choice === KpiNextActionResultChoice.Practiced
  )
}
